import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  input,
  output,
  signal,
  ViewEncapsulation,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatDividerModule } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { RouterModule } from '@angular/router';
import { SubscriptionSummaryDto } from '@workspace/data-types';
import {
  AlertSnackBarService,
  AvatarAuraComponent,
  ConfirmDialogAuraComponent,
  LuxonDatePipe,
} from '@workspace/ng-aura';
import { PriceDisplayPipe, SubscriptionStatusDisplayPipe } from '@workspace/ng-data-access';
import { ApiService } from '@workspace/ng-http';
import { catchError, filter, finalize, of, switchMap, tap } from 'rxjs';

@Component({
  selector: 'core-subscription-details',
  templateUrl: './subscription-details.component.html',
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    SubscriptionStatusDisplayPipe,
    PriceDisplayPipe,
    MatIconModule,
    MatDividerModule,
    MatButtonModule,
    LuxonDatePipe,
    AvatarAuraComponent,
    RouterModule,
  ],
})
export class SubscriptionDetailsCoreComponent {
  private api = inject(ApiService);
  private alertSnackbar = inject(AlertSnackBarService);
  private destroyRef = inject(DestroyRef);
  private dialog = inject(MatDialog);

  subscription = input.required<SubscriptionSummaryDto>();
  subscriptionUpdated = output<SubscriptionSummaryDto>();

  isCancelling = signal<boolean>(false);

  readonly canCancel = computed(() => {
    const sub = this.subscription();
    return sub && (sub.status === 'active' || sub.status === 'trialing') && !sub.cancelAtPeriodEnd;
  });

  cancelSubscription(): void {
    const sub = this.subscription();
    if (!sub) return;

    this.dialog
      .open(ConfirmDialogAuraComponent, {
        data: {
          title: "Annuler l'abonnement",
          message: 'Êtes-vous sûr de vouloir annuler cet abonnement à la fin de la période en cours ?',
          confirm: {
            text: "Annuler l'abonnement",
            class: 'primary destructive',
          },
        },
      })
      .afterClosed()
      .pipe(
        filter((confirmed) => !!confirmed),
        tap(() => this.isCancelling.set(true)),
        switchMap(() => this.api.admin().subscriptions().cancel(sub._id)),
        tap((updatedSub) => {
          this.alertSnackbar.success({
            title: 'Abonnement annulé',
            message: "L'abonnement sera annulé à la fin de la période de facturation.",
          });
          this.subscriptionUpdated.emit(updatedSub);
        }),
        catchError((err) => {
          this.alertSnackbar.error({
            title: "Erreur d'annulation",
            message: "Une erreur est survenue lors de l'annulation de l'abonnement.",
          });
          console.error(err);
          return of(null);
        }),
        finalize(() => this.isCancelling.set(false)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }
}
